'use client'

import { useTranslation } from '@/i18n/client'
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons'
import { Button, Layout, theme } from 'antd'
import { useCompanyQuery } from 'graphql/query/company.query'
import Link from 'next/link'
import { getLuminance } from 'polished'
import posthog from 'posthog-js'
import React, { useEffect, useMemo } from "react";
import { FaDraftingCompass } from 'react-icons/fa'
import { useAppDispatch, useAppSelector } from 'store'
import { toggle } from 'store/menu'
import styled, { css } from 'styled-components'
import { useEditorSize } from '../hooks/use.editor.size'
import { useWindow } from '../hooks/use.window'
import { useWindowScroll } from '../hooks/use.window.scroll'
import { getPublicEnv } from '@tucan/utils'
import { MENU_TOGGLE_WIDTH } from './layout/menu/base.menu'
import tucanLogoDark from './logo/tucan.logo.dark.svg'
import tucanLogo from './logo/tucan.png'
import tucanLogoWhite from './logo/tucan.white.svg'
import { SearchTag } from './record/tag/search.tag'
import { ToolbarUserMenu } from './toolbar.user.menu'

const StyledHeader = styled(Layout.Header)<{
  $background?: string
  $backgroundImage?: string
}>`
  display: flex;
  flex-direction: row;
  position: fixed;
  align-items: center;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding-right: 32px;
  padding-left: 32px;
  transition: box-shadow 0.3s ease-in-out;
  
  ${props => props.$background !== 'transparent' && css`
    background: ${props.$background};
  `}

  ${props => props.$background === 'transparent' && props.$backgroundImage && css`
    background-image: url(${props.$backgroundImage});
    background-size: cover;
    background-position: top;
    background-attachment: fixed;
  `}
  
  &.scroll {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 650px) {
    padding-right: 10px;
    padding-left: 10px;

    div.menu {
      display: none;
    }
  }
`

type Props = {
  hasSideMenu?: boolean
}

export const Toolbar : React.FC<Props> = (props) => {
  const { i18n } = useTranslation()
  const { token } = theme.useToken()
  const companyId = useAppSelector(state => state.company.id)
  const collapsed = useAppSelector(state => state.menu.collapsed)
  const authenticated = useAppSelector(state => state.auth.authenticated)
  const dispatch = useAppDispatch()
  const {
    width,
    visible: { helper: showTagSearch },
  } = useEditorSize()
  const canToggle = props.hasSideMenu && width && width < MENU_TOGGLE_WIDTH
  const company = useCompanyQuery({
    skip: !companyId,
    variables: {
      id: String(companyId),
    },
  })

  useEffect(() => {
    if (!company.data) {
      return
    }
    posthog.group("company", company.data.company.id, {
      company_name: company.data.company.name,
    });
  }, [company.data]);

  const scroll = useWindowScroll(1)
  const window = useWindow()

  const logo = useMemo(() => {
    if (width < 700) {
      if (getPublicEnv('ICON_URL')) {
        return (
          <img
            src={getPublicEnv('ICON_URL')}
            alt='Tucan.ai'
            style={{ height: 40 }}
          />
        )
      }

      return (
        <img
          src={tucanLogo.src}
          alt='Tucan.ai GmbH'
          style={{ height: 40 }}
        />
      )
    }

    if (getPublicEnv('LOGO_URL')) {
      return (
        <img
          src={getPublicEnv('LOGO_URL')}
          alt='Tucan.ai'
          style={{ height: 40 }}
        />
      )
    }

    if (getLuminance(token.colorBgBase) > 0.5) {
      return (
        <img
          src={tucanLogoDark.src}
          alt='Dark Tucan Logo'
          style={{ height: 40 }}
        />
      )
    }

    return (
      <img
        src={tucanLogoWhite.src}
        alt='White Tucan Logo'
        style={{ height: 40 }}
      />
    )
  }, [width, token.colorBgBase])

  return (
    (<StyledHeader
      className={scroll > 10 ? 'scroll' : undefined}
      $background={token.colorBgLayout}
      $backgroundImage={getPublicEnv('BACKGROUND_URL')}
    >
      {canToggle && (
        <div style={{ marginLeft: 8, marginRight: 16 }}>
          <Button
            shape='circle'
            onClick={() =>
              dispatch(toggle(collapsed === undefined ? false : !collapsed))
            }
            icon={
              collapsed === undefined || collapsed ? (
                <MenuUnfoldOutlined />
              ) : (
                <MenuFoldOutlined />
              )
            }
          />
        </div>
      )}
      <Link href={`/${i18n.language}/app`} style={{ marginRight: 16 }}>
        {logo}
      </Link>
      {authenticated && !/app/.test(window?.location.pathname) && (
        <Link href={`/${i18n.language}/app`} passHref>
          <Button
            style={{ marginLeft: 32 }}
            type={'primary'}
            icon={
              <FaDraftingCompass
                className={'reactIcon'}
                style={{ marginRight: 8, paddingTop: 4 }}
              />
            }
          >
              HOME
          </Button>
        </Link>
      )}
      {/*<ToolbarSearch />*/}
      <div style={{ flex: '1' }} />
      {showTagSearch && <SearchTag />}
      {authenticated && (
        <ToolbarUserMenu companyName={company?.data?.company?.name ?? 'Company'} />
      )}
    </StyledHeader>)
  )
}
